import { Parm0Callback } from "@Components/app/members/teamInterfaces";
import config from "@Config";
import { AuthSecurityLevel, DeviceFamilies, DeviceModels, Preferences } from "@Enums";
import {
  GameplayEvent,
  IncomingBybCatEvent,
  IncomingReplayEvent,
  IncomingReplayUiEvent,
  IncomingScenarioRetryReplay,
  NavigationEvent,
  SetHeaderAltSectionsEvent,
  VRHeaderButtonEvent,
} from "@Globals/types";
import Event from "@Helpers/event";
import { UploadCompleteEvent } from "@Services/upload-service";
import { PlaySessionEvent } from "@Types/unity-events";
import { ActionError } from "./initial-state.constants";
import { AccountState, GlobalState } from "./initial-state.types";

let initialState: GlobalState = {
  notifications: {
    minimumClientVersion: config.minimumClientVersion,
    events: {},
  },
  preferences: {
    [Preferences.allow_push_notifications]: null,
    events: {
      pushNotificationsUpdated: new Event<{ permission: boolean; callback?: (data: any) => void }>(),
    },
  },
  device: {
    // info: device,
    isLandscape: true,
    isUnityEmbedded: process.config.MOCK_BRIDGE || false,
    isVR: !!process.config.MOCK_VR,
    isMobile: !!process.config.MOCK_MOBILE,
    family: DeviceFamilies.Unknown,
    model: DeviceModels.Unknown,
    events: {},
  },
  audio: {
    events: {
      playSound: new Event<{ src: string; volume?: number }>(),
    },
  },
  app: {
    availableRoutes: [],
    activeBasePath: null,
    appBarHeight: 0,
    navBarHeight: 0,
    webVersion: config.webVersion,
    clientVersion: config.placeholderClientVersion,
    bridgeInitialized: false,
    applicationIdentifier: null,
    initialized: false,
    passThrough: false,
    isFullscreen: false,
    exclusiveMode: false,
    hideNotifications: false,
    navDrawerOpen: false,
    challengesEnabled: true,
    mode: "dark",
    events: {
      notificationEvent: new Event<{ action: string; data: any }>(),
      showTooltip: new Event(),
      moveTooltip: new Event(),
      hideTooltip: new Event(),
      tabBarVisible: new Event(),
      bottomNavMobileHidden: new Event<boolean>(),
      navBarHidden: new Event<{ hidden: boolean }>(),
      exclusivePageEntered: new Event(),
      exclusivePageExited: new Event(),
      showWizardDialog: new Event(),
      hideWizardDialog: new Event(),
      triggerBackNavigation: new Event(),
      bridgeInitialized: new Event(),
      passThroughStateChanged: new Event(),
      replayFrame: new Event(),
      replayPassOptions: new Event(),
      replayDuration: new Event(),
      enterReplayMode: new Event(),
      exitReplayMode: new Event(),
      animatePage: new Event<{
        path: string;
        onAnimateOutReceived?: () => void;
        onAnimateComplete?: ({ lastPath }: { lastPath?: string }) => void;
      }>(),
      navigate: new Event<NavigationEvent>(),
      navigateExclusive: new Event<NavigationEvent>(),
      navigateBack: new Event(),
      navigateBackExclusive: new Event(),
      forceShowPage: new Event(),
      showScenarioResults: new Event(),
      showPlaylistResults: new Event(),
      gameplayEntered: new Event<GameplayEvent>(),
      gameplayExited: new Event<GameplayEvent>(),
      gameReadyEvent: new Event(),
      playSessionResultsReady: new Event(),
      hideBreadcrumbs: new Event(),
      showBreadcrumbs: new Event(),
      isAppLinkedResolved: new Event(),
      appFocusLost: new Event(),
      appFocusRestored: new Event(),
      replayEvent: new Event<IncomingReplayEvent>(),
      replayUiEvent: new Event<IncomingReplayUiEvent>(),
      bybCatEvent: new Event<IncomingBybCatEvent>(),
      playSessionEvent: new Event<PlaySessionEvent>(),
      scenarioRetryReplay: new Event<IncomingScenarioRetryReplay>(),
      showVideo: new Event(),
      hideVideo: new Event(),
      setProductSelectionState: new Event<{ disabled: boolean }>(),
      setBootcampHeaderAction: new Event<"skip" | "complete">(),
      showHeaderButton: new Event<VRHeaderButtonEvent>(),
      hideHeaderButton: new Event(),
      setHeaderAltSections: new Event<SetHeaderAltSectionsEvent>(),
    },
  },
  analytics: {
    events: {
      analyticsEvent: new Event(),
    },
  },
  editor: {
    events: {
      editorSaveAsNewScenario: new Event(),
      editorSaveScenarioChanges: new Event(),
      editorEntered: new Event(),
      editorExited: new Event(),
    },
  },
  auth: {
    lastAuthDTS: null,
    errorMessage: "",
    signUpComplete: false,
    events: {
      authStateChanged: new Event(),
      loginSuccess: new Event(),
      logout: new Event(),
      accountInfoUpdated: new Event(),
      passwordUpdated: new Event(),
      resetCodeSent: new Event(),
      passwordReset: new Event(),
      subscriptionAccessGranted: new Event(),
      subscriptionsResolved: new Event(),
      userAccountCreated: new Event(),
      userEmailVerified: new Event(),
      reAuthRequired: new Event<{ securityLevel: AuthSecurityLevel; onSuccess?: Parm0Callback; onCancel?: Parm0Callback }>(),
      switchProfile: new Event(),
      switchProfileSuccess: new Event(),
    },
  },
  account: {
    id: null,
    name: null,
    stripe_customer_id: null,
    authorizations: [],
    is_business: false,
    created_at: null,
    updated_at: null,
    user_role: null,
    total_seats: 0,
    assigned_seats: 0,
    available_seats: 0,
    data: null,
    accounts_data: null,
    events: {
      switchAccount: new Event<{ account_id: number }>(),
      refreshCurrentAccount: new Event<{ onSuccess?: () => void }>(),
      resetAccountState: new Event(),
    },
    tracking_providers: [],
    is_resolved: false,
  } as AccountState,
  user: {
    // Flag if the active user AppUUID matches the the remote app linked auth account
    account_id: null,
    accountLinkedUuIds: [],
    noAccount: false,
    needsUserRecordRecovery: false,
    needsAccountProductsRecovery: false,
    isAppLinkedToUser: false,
    emailVerified: false,
    hasAccessSubscriptions: false,
    hasAllAccessSubscription: false,
    hasAllAccessExpired: false,
    allAccessExpiryDate: null,
    hasBootcampSubscription: false,
    hasBootcampAccessExpired: false,
    bootCampExpiryDate: null,
    hasPlaylistCMSAccess: false,
    isSignupWorkflow: false,
    progressionConfig: null,
    data: null,
    userRating: null,
    level: null,
    authenticated: false,
    displayName: null,
    isSuperAdmin: false,
    adminForClubs: [],
    adminForTeams: [],
    teams: [],
    creationDate: null,
    dob: null,
    id: null,
    email: null,
    first: null,
    gender: null,
    last: null,
    countryCode: null,
    licenseProvider: null,
    role: null,
    recentProgress: null,
    associatedTeams: [],
    appUuID: null,
    profileAllowance: 0,
    providerTeamId: "",
    accessTeamId: "",
    avatar: "",
    hasPin: null,
    events: {},
    groupInvites: [],
    groupInvitesResolved: false,
    dbId: null,
    inBootcamp: false,
  },
  error: {
    active: false,
    action: "",
    message: "",
    title: "",
    type: ActionError.Retry,
    retryHandler: null,
  },
  loading: {
    active: true,
    spinner: true,
    message: null,
    events: {
      enableLoader: new Event(),
      resetLoader: new Event(),
    },
  },
  upload: {
    events: {
      processPendingUploads: new Event(),
      uploadComplete: new Event<UploadCompleteEvent>(),
      projectUploadErrors: new Event<{ projectId: number; errorCount: number }>(),
    },
  },

  // Admin Only Context
  admin: {
    users: {
      tableData: [],
      data: [],
      potentialClubAdmins: [],
      potentialTeamAdmins: [],
      potentialTeamMembers: [],
      events: {
        userUpdated: new Event(),
        userCreated: new Event(),
        userDisabled: new Event(),
        userEnabled: new Event(),
        userDeleted: new Event(),
        getUserSubscriptionsSuccess: new Event(),
      },
    },
    teams: {
      tableData: [],
      data: [],
      unassignedTeams: [],
      events: {
        teamUpdated: new Event(),
        teamCreated: new Event(),
        teamDeleted: new Event(),
      },
    },
    subscriptions: {
      list: [],
      events: {
        onSubscriptionsListUpdated: new Event(),
      },
    },
    clubs: {
      tabledData: [],
      data: [],
      events: {
        clubUpdated: new Event(),
        clubCreated: new Event(),
      },
    },
  },
};

export default initialState;
